import { format } from 'date-fns';

/** Valor de `<input type="datetime-local">` no fuso do navegador. */
export function paraCampoLocal(iso: string | null | undefined): string {
  return iso ? format(new Date(iso), "yyyy-MM-dd'T'HH:mm") : '';
}

/** Do campo local para ISO com fuso — o que a API exige (RN-CP-01). */
export function doCampoLocal(valor: string): string {
  return new Date(valor).toISOString();
}

/**
 * Contrato da tela `/admin/parametros/ciclos-inscricao` com a API
 * (`apps/api/src/modulos/ciclo-inscricao`, rotas `bff/v1/admin/ciclos-inscricao`).
 * Mesmo desenho publicado em `docs/credenciamento-ponta-a-ponta/frentes/F03.md`.
 */

/** As quatro modalidades do credenciamento, na ordem do edital (D-15). */
export const MODALIDADES = ['CONVENCIONAL', 'ACESSIVEL', 'ESPECIAL', 'CRECHE'] as const;
export type Modalidade = (typeof MODALIDADES)[number];

export const ROTULO_DA_MODALIDADE: Record<Modalidade, string> = {
  CONVENCIONAL: 'TEG comum',
  ACESSIVEL: 'Acessível',
  ESPECIAL: 'Especial',
  CRECHE: 'Creche',
};

export type StatusDoCiclo = 'RASCUNHO' | 'ABERTO' | 'ENCERRADO' | 'SORTEADO' | 'CANCELADO';

export interface Dre {
  id: string;
  codigo: string;
  nome: string;
}

export interface Vaga {
  dreId: string;
  modalidade: Modalidade;
  quantidade: number;
}

export interface CicloResumo {
  id: string;
  nome: string;
  status: StatusDoCiclo;
  inicioEm: string | null;
  fimEm: string | null;
  editalVersao: string | null;
  maxEscolas: number | null;
  prazoRespostaDias: number | null;
  versao: number;
  totalVagas: number;
  combinacoes: number;
  totalInscricoes: number;
  editalEnviado: boolean;
  aceitaInscricoes: boolean;
}

export interface EventoDoCiclo {
  id: string;
  acao: string;
  origem: 'USUARIO' | 'SISTEMA';
  autor: string | null;
  estadoAnterior: string | null;
  estadoFinal: string;
  motivo: string | null;
  alteracoes: string[];
  criadoEm: string;
}

export interface CicloDetalhe {
  ciclo: CicloResumo & {
    descricao: string | null;
    abertoEm: string | null;
    encerradoEm: string | null;
    sorteadoEm: string | null;
    canceladoEm: string | null;
    motivoCancelamento: string | null;
    atualizadoEm: string;
  };
  vagas: (Vaga & { dreNome: string })[];
  edital: { arquivoNome: string; tamanhoBytes: number; sha256: string; enviadoEm: string } | null;
  eventos: EventoDoCiclo[];
}

export interface ListaDeCiclos {
  itens: CicloResumo[];
  total: number;
  pagina: number;
  tamanho: number;
  dres: Dre[];
  modalidades: Modalidade[];
  cicloAbertoId: string | null;
}

/** O que o formulário manda para criar ou alterar. */
export interface EntradaDoCiclo {
  nome: string;
  descricao?: string;
  inicioEm: string;
  fimEm: string;
  editalVersao: string;
  maxEscolas: number;
  prazoRespostaDias: number;
  vagas: Vaga[];
}

/**
 * Situação para a tela. ABERTO antes do início é "Agendado": o público já vê as
 * datas, mas o autocadastro ainda não abre.
 */
export function situacaoDoCiclo(
  c: Pick<CicloResumo, 'status' | 'aceitaInscricoes' | 'inicioEm'>,
  agora = Date.now(),
): {
  rotulo: string;
  variante: 'rascunho' | 'ativo' | 'pendente' | 'inativo' | 'processado' | 'cancelado';
} {
  switch (c.status) {
    case 'RASCUNHO':
      return { rotulo: 'Rascunho', variante: 'rascunho' };
    case 'ABERTO':
      if (c.aceitaInscricoes) return { rotulo: 'Aberto', variante: 'ativo' };
      if (c.inicioEm && new Date(c.inicioEm).getTime() > agora)
        return { rotulo: 'Agendado', variante: 'pendente' };
      return { rotulo: 'Encerrando', variante: 'inativo' };
    case 'ENCERRADO':
      return { rotulo: 'Encerrado', variante: 'inativo' };
    case 'SORTEADO':
      return { rotulo: 'Sorteado', variante: 'processado' };
    default:
      return { rotulo: 'Cancelado', variante: 'cancelado' };
  }
}

export const ROTULO_DA_ACAO: Record<string, string> = {
  CRIAR: 'Ciclo criado',
  ALTERAR: 'Ciclo alterado',
  ENVIAR_EDITAL: 'Edital enviado',
  ABRIR: 'Ciclo aberto',
  ENCERRAR: 'Ciclo encerrado',
  SORTEAR: 'Sorteio realizado',
  CANCELAR: 'Ciclo cancelado',
  SUSPENDER: 'Ciclo suspenso',
  PRORROGAR: 'Ciclo prorrogado',
};

export const ROTULO_DO_CAMPO: Record<string, string> = {
  nome: 'nome',
  descricao: 'descrição',
  inicioEm: 'início',
  fimEm: 'fim',
  editalVersao: 'versão do edital',
  maxEscolas: 'teto de escolas',
  prazoRespostaDias: 'prazo de resposta',
  vagas: 'vagas',
  edital: 'edital',
  status: 'situação',
};
